import type { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  guestFieldCatalogueKey,
  rolePreviewEditorFieldsKey,
  rolePreviewScopeTemplateKey,
  roleTemplatesKey,
} from "@/lib/authz/roleTemplateQueryKeys";

/**
 * B2 (plan Rev. 5.2, FE-3 "Invalidation after every GUEST field
 * create/update/delete").
 *
 * A GUEST field create, rename, delete or sensitivity raise runs the
 * backend's revoke cascades on the event's role templates: the key is pulled
 * from `fieldAllowlist` / `fieldExclusions` and every touched template's
 * `version` is bumped. Every cached copy of a template is then stale — and a
 * stale `version` is a 409 `versionMismatch` on the next save. So after such a
 * change the FE marks them all stale:
 *
 * - `roleTemplatesKey` — the Roles list; it is also the PREFIX of every
 *   single-template key (`roleTemplateKey`), so this one call reaches the role
 *   editor's fresh single GET too;
 * - `rolePreviewScopeTemplateKey(eventId)` — the preview-bar editor's read of
 *   the previewed template (event prefix, every template id);
 * - `rolePreviewEditorFieldsKey(eventId)` — the preview-bar editor's typed
 *   field read (row filters), so a new field shows there at once;
 * - `guestFieldCatalogueKey` — the loud guest field catalogue the editor, the
 *   preview bar and the Roles badge share. It lives under
 *   `["additionalFields", eventId]`; a caller that already invalidates that
 *   family by prefix (`FieldsProvider.invalidateFieldQueries`) passes
 *   `catalogue: false` instead of marking it twice.
 *
 * Only real events: `"root"` (the root dashboard's provider) and `"0"` have
 * no role templates.
 */
export function isRoleTemplateEventId(eventId: string | null | undefined): eventId is string {
  return !!eventId && eventId !== "root" && eventId !== "0";
}

export async function invalidateRoleTemplateViews(
  queryClient: QueryClient,
  eventId: string,
  { catalogue = true }: { catalogue?: boolean } = {},
): Promise<void> {
  if (!isRoleTemplateEventId(eventId)) return;
  const keys: QueryKey[] = [
    roleTemplatesKey(eventId),
    rolePreviewScopeTemplateKey(eventId),
    rolePreviewEditorFieldsKey(eventId),
  ];
  if (catalogue) keys.push(guestFieldCatalogueKey(eventId));
  await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
}

/**
 * Whether `key` is one of the role-template reads
 * {@link invalidateRoleTemplateViews} marks stale — the Roles list with every
 * single template under it, and the preview-bar editor's reads — for a caller
 * that invalidates by predicate (Velo's live-sync bridge, `AiDataSyncBridge`).
 * With `eventId`, only that event's; without, any real event's. The catalogue
 * is not matched: it is an `["additionalFields", eventId]` key, which such a
 * caller covers by prefix already.
 */
export function isRoleTemplateViewKey(key: QueryKey, eventId?: string): boolean {
  const event = key[1];
  if (typeof event !== "string" || !isRoleTemplateEventId(event)) return false;
  if (eventId !== undefined && event !== eventId) return false;
  return [roleTemplatesKey(event), rolePreviewScopeTemplateKey(event), rolePreviewEditorFieldsKey(event)].some((prefix) =>
    prefix.every((part, i) => key[i] === part),
  );
}
